import { useMemoizedFn } from 'ahooks';
import { min } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Button, EnhancedTable, Link, MessagePlugin, Popconfirm, Space } from 'tdesign-react';
import { produce } from 'immer';
import { GesturePressIcon, RootListIcon, TreeListIcon } from 'tdesign-icons-react';
import ListPanel from '@/components/ListPanel';
import useTable from '@/hooks/useTable';
import { deleteMenu, getMenuList } from '@/services/menuService';
import { renderDateTime, renderOptions } from '@/utils/common';
import { menuTypeOptions } from '@/utils/constant';
import PermWrapper from '@/components/PermWrapper';
import FormModal from './FormModal';

// 添加一些图标
const menuOptions = produce(menuTypeOptions, (draft) => {
  draft.forEach((item) => {
    switch (item.value) {
      case 0:
        item.label = (
          <Space size={4} align='center'>
            菜单 <TreeListIcon />
          </Space>
        );
        break;
      case 1:
        item.label = (
          <Space size={4} align='center'>
            按钮 <GesturePressIcon />
          </Space>
        );
        break;
      case 2:
        item.label = (
          <Space size={4} align='center'>
            内容 <RootListIcon />
          </Space>
        );
        break;
      default:
        break;
    }
  });
});

export default function Menu() {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(null);

  const columns = [
    {
      title: '资源名称',
      colKey: 'label',
    },
    {
      title: '类型',
      colKey: 'type',
      options: menuOptions,
      cell: renderOptions,
      width: 140,
    },
    {
      title: '资源地址',
      colKey: 'location',
      align: 'center',
    },
    {
      title: '创建时间',
      colKey: 'createTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='pulsar:sys:menu:update'>
            <Link
              theme='primary'
              onClick={() => {
                setDetail(row);
                setVisible(true);
              }}
              hover='color'
            >
              编辑
            </Link>
          </PermWrapper>
          <PermWrapper resource='pulsar:sys:menu:delete'>
            <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
              <Link theme='danger' hover='color'>
                删除
              </Link>
            </Popconfirm>
          </PermWrapper>
        </Space>
      ),
      width: 120,
    },
  ];

  const loadData = useMemoizedFn(async () => {
    const { data } = await getMenuList();
    setData(data);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
    noOptimize: true, // 树状结构不允许优化
    operators: (
      <PermWrapper resource='pulsar:sys:menu:create'>
        <Button onClick={() => setVisible(true)}>新增资源</Button>
      </PermWrapper>
    ),
  });

  const doDelete = async (row) => {
    await deleteMenu(row.id);
    MessagePlugin.success('操作成功');
    doRefresh();
  };

  // 特别处理分页，原分页无效
  const getData = useMemoizedFn((data) => {
    const { current, pageSize, total } = config.pagination;
    const from = (current - 1) * pageSize;
    const to = min([current * pageSize, total]);
    return data?.slice(from, to);
  });

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const handleClose = (refresh) => {
    setVisible(false);
    setDetail(null);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <ListPanel header='资源管理'>
      {header}
      <EnhancedTable {...config} data={getData(config.data)} tree={{ childrenKey: 'children' }} />
      <FormModal
        treeData={config.data}
        visible={visible}
        detail={detail}
        handleClose={handleClose}
      />
    </ListPanel>
  );
}
